// Analytics service without Firebase Analytics
// This can be replaced with any other analytics provider like Google Analytics, Plausible, etc.

/**
 * Analytics service that logs events to console
 * Replace this with your preferred analytics provider
 */
const consoleAnalytics = {
  logEvent: (name: string, params?: Record<string, any>) => {
    console.log(`[Analytics] Event: ${name}`, params || {});
    return Promise.resolve();
  },
  setUserId: (id: string) => {
    console.log(`[Analytics] Set User ID: ${id}`);
    return Promise.resolve();
  },
  setUserProperties: (properties: Record<string, string>) => {
    console.log(`[Analytics] Set User Properties:`, properties);
    return Promise.resolve();
  },
  setAnalyticsCollectionEnabled: (enabled: boolean) => {
    console.log(`[Analytics] Collection ${enabled ? 'Enabled' : 'Disabled'}`);
    return Promise.resolve();
  }
};

/**
 * Service to handle Analytics events
 */
export const analyticsService = {
  /**
   * Initialize analytics
   */
  initializeDebugMode: async (): Promise<void> => {
    try {
      console.log('Analytics service initialized');
    } catch (error) {
      console.warn('Error initializing analytics:', error);
    }
  },

  /**
   * Track a page view event
   * @param pageName The name of the page viewed
   */
  trackPageView: async (pageName: string): Promise<void> => {
    try {
      consoleAnalytics.logEvent('page_view', {
          page_title: pageName,
          page_location: window.location.href,
          page_path: window.location.pathname
        });
    } catch (error) {
      console.warn('Error tracking page view:', error);
    }
  },

  /**
   * Track a quotation generation event
   * @param quotationData Data about the quotation
   */
  trackQuotationGenerated: async (quotationData: {
    distance: number;
    vehicleType: string;
    trailerType?: string;
    cargoType?: string;
    totalCost: number;
    currency: string;
  }): Promise<void> => {
    try {
      const eventParams = {
        distance: quotationData.distance,
        vehicle_type: quotationData.vehicleType,
        trailer_type: quotationData.trailerType || 'none',
        cargo_type: quotationData.cargoType || 'not_specified',
        total_cost: quotationData.totalCost,
        currency: quotationData.currency
      };
      
      consoleAnalytics.logEvent('quotation_generated', eventParams);
    } catch (error) {
      console.warn('Error tracking quotation generation:', error);
    }
  },

  /**
   * Track a quotation download event
   * @param quotationId ID of the quotation
   */
  trackQuotationDownloaded: async (quotationId: string): Promise<void> => {
    try {
      const eventParams = { quotation_id: quotationId };
      
      consoleAnalytics.logEvent('quotation_downloaded', eventParams);
    } catch (error) {
      console.warn('Error tracking quotation download:', error);
    }
  },

  /**
   * Track a quotation saved event
   * @param quotationId ID of the quotation
   */
  trackQuotationSaved: async (quotationId: string): Promise<void> => {
    try {
      const eventParams = { quotation_id: quotationId };
      
      consoleAnalytics.logEvent('quotation_saved', eventParams);
    } catch (error) {
      console.warn('Error tracking quotation save:', error);
    }
  },

  /**
   * Track a token purchase event
   * @param packageInfo Information about the token package purchased
   */
  trackTokenPurchase: async (packageInfo: {
    packageId: string;
    tokens: number;
    amount: number;
    currency: string;
  }): Promise<void> => {
    try {
      const eventParams = {
        package_id: packageInfo.packageId,
        tokens: packageInfo.tokens,
        amount: packageInfo.amount,
        currency: packageInfo.currency,
        value: packageInfo.amount,
        items: [{
          item_id: packageInfo.packageId,
          item_name: `Token Package - ${packageInfo.tokens} tokens`,
          price: packageInfo.amount,
          quantity: 1
        }]
      };
      
        // Use standard ecommerce event for better reporting
      consoleAnalytics.logEvent('purchase', eventParams);
    } catch (error) {
      console.warn('Error tracking token purchase:', error);
    }
  },

  /**
   * Track user sign-in event
   * @param method The method used for sign-in (email, etc.)
   */
  trackSignIn: async (method: string): Promise<void> => {
    try {
      const eventParams = { method: method };
      
      consoleAnalytics.logEvent('login', eventParams);
    } catch (error) {
      console.warn('Error tracking sign-in:', error);
    }
  },

  /**
   * Track user sign-up event
   * @param method The method used for sign-up (email, etc.)
   */
  trackSignUp: async (method: string): Promise<void> => {
    try {
      const eventParams = { method: method };
      
      consoleAnalytics.logEvent('sign_up', eventParams);
    } catch (error) {
      console.warn('Error tracking sign-up:', error);
    }
  },

  /**
   * Set user ID for analytics
   * @param userId User ID to associate with analytics events
   */
  setUserId: async (userId: string): Promise<void> => {
    try {
      consoleAnalytics.setUserId(userId);
    } catch (error) {
      console.warn('Error setting user ID for analytics:', error);
    }
  },

  /**
   * Set user properties for analytics
   * @param properties User properties to set
   */
  setUserProperties: async (properties: Record<string, string>): Promise<void> => {
    try {
      consoleAnalytics.setUserProperties(properties);
    } catch (error) {
      console.warn('Error setting user properties for analytics:', error);
    }
  },

  /**
   * Track a custom event
   * @param eventName Name of the custom event
   * @param params Parameters for the event
   */
  trackCustomEvent: async (eventName: string, params?: Record<string, any>): Promise<void> => {
    try {
      consoleAnalytics.logEvent(eventName, params);
    } catch (error) {
      console.warn(`Error tracking custom event ${eventName}:`, error);
    }
  }
};